import { type CountryCode, LocaleSetting, localeSettings } from '@eversports/locale'
import { ImmutableMap } from '@eversports/maps'
import { getStageFromDeployEnvironmentLegacy, type Stage } from '@eversports/stage'

interface CountryConfigConstructorOptions {
  countryCode: CountryCode
  hostname: string
  localeSettings: LocaleSetting
  internalHostname?: string
}

export class CountryConfig {
  public countryCode: CountryCode
  public homepage: string
  public localeSettings: LocaleSetting

  /**
   * The hostname is the publicly exposed hostname served through Cloudflare
   * This one is also used to populate the homepage
   */
  public hostname: string

  /**
   * The internal hostname is the host of our ecs services directly (Served via cloudfront)
   * Only used to determine the country config based on a request
   * since our cloudflare workers are directly requesting the localed internal ECS hostnames when rolled out
   */
  public internalHostname?: string

  constructor({ countryCode, hostname, localeSettings, internalHostname }: CountryConfigConstructorOptions) {
    this.countryCode = countryCode
    this.hostname = hostname
    this.homepage = `https://${this.hostname}`
    this.internalHostname = internalHostname
    this.localeSettings = localeSettings
  }
}

const localeAt = localeSettings.get('de-AT')
const localeDe = localeSettings.get('de-DE')
const localeCh = localeSettings.get('de-CH')
const localeNl = localeSettings.get('nl-NL')
const localeFr = localeSettings.get('fr-FR')
const localeEs = localeSettings.get('es-ES')
const localeIt = localeSettings.get('it-IT')
const localeBe = localeSettings.get('fr-BE')
if (!localeAt || !localeDe || !localeCh || !localeNl || !localeFr || !localeEs || !localeIt || !localeBe) {
  throw new Error(`Locale not found`)
}

const COUNTRY_CONFIG_HOSTNAME_OVERWRITE_ENABLED = process.env.COUNTRY_CONFIG_HOSTNAME_OVERWRITE_ENABLED === 'true'

let deployEnvironment = getStageFromDeployEnvironmentLegacy()

if (COUNTRY_CONFIG_HOSTNAME_OVERWRITE_ENABLED) {
  // Make it possible to overwrite which deploy environment should be used for the country config
  // Needed preview environments because there the deploy environment is always set to `test`
  // but we wanna get the dev country config
  deployEnvironment =
    (process.env.COUNTRY_CONFIG_DEPLOY_ENVIRONMENT_OVERWRITE as Stage) || getStageFromDeployEnvironmentLegacy()
}

/**
 * Delivers the internal hostname for the given country code and stage
 * Those are our internal ECS hostnames
 */
const getInternalHostname = (stage: string, countryCode: string): string | undefined => {
  if (!COUNTRY_CONFIG_HOSTNAME_OVERWRITE_ENABLED || !process.env.COUNTRY_CONFIG_HOSTNAME_OVERWRITE) {
    return undefined
  }

  return `www-${stage}-${countryCode.toLowerCase()}${process.env.COUNTRY_CONFIG_HOSTNAME_OVERWRITE}`
}

const countryConfigMap: Record<Stage, Array<CountryConfig>> = {
  dev: [
    new CountryConfig({
      countryCode: 'AT',
      internalHostname: getInternalHostname('dev', 'AT'),
      hostname: getInternalHostname('dev', 'AT') || `www-dev-at.eversports.local`,
      localeSettings: localeAt,
    }),
    new CountryConfig({
      countryCode: 'DE',
      internalHostname: getInternalHostname('dev', 'DE'),
      hostname: getInternalHostname('dev', 'DE') || `www-dev-de.eversports.local`,
      localeSettings: localeDe,
    }),
    new CountryConfig({
      countryCode: 'CH',
      internalHostname: getInternalHostname('dev', 'CH'),
      hostname: getInternalHostname('dev', 'CH') || `www-dev-ch.eversports.local`,
      localeSettings: localeCh,
    }),
    new CountryConfig({
      countryCode: 'NL',
      internalHostname: getInternalHostname('dev', 'NL'),
      hostname: getInternalHostname('dev', 'NL') || `www-dev-nl.eversports.local`,
      localeSettings: localeNl,
    }),
    new CountryConfig({
      countryCode: 'FR',
      internalHostname: getInternalHostname('dev', 'FR'),
      hostname: getInternalHostname('dev', 'FR') || `www-dev-fr.eversports.local`,
      localeSettings: localeFr,
    }),
    new CountryConfig({
      countryCode: 'ES',
      internalHostname: getInternalHostname('dev', 'ES'),
      hostname: getInternalHostname('dev', 'ES') || `www-dev-es.eversports.local`,
      localeSettings: localeEs,
    }),
    new CountryConfig({
      countryCode: 'IT',
      internalHostname: getInternalHostname('dev', 'IT'),
      hostname: getInternalHostname('dev', 'IT') || `www-dev-it.eversports.local`,
      localeSettings: localeIt,
    }),
    new CountryConfig({
      countryCode: 'BE',
      internalHostname: getInternalHostname('dev', 'BE'),
      hostname: getInternalHostname('dev', 'BE') || `www-dev-be.eversports.local`,
      localeSettings: localeBe,
    }),
  ],
  test: [
    new CountryConfig({
      countryCode: 'AT',
      internalHostname: getInternalHostname('test', 'AT'),
      hostname: `www-test-at.eversports.dev`,
      localeSettings: localeAt,
    }),
    new CountryConfig({
      countryCode: 'DE',
      internalHostname: getInternalHostname('test', 'DE'),
      hostname: `www-test-de.eversports.dev`,
      localeSettings: localeDe,
    }),
    new CountryConfig({
      countryCode: 'CH',
      internalHostname: getInternalHostname('test', 'CH'),
      hostname: `www-test-ch.eversports.dev`,
      localeSettings: localeCh,
    }),
    new CountryConfig({
      countryCode: 'NL',
      internalHostname: getInternalHostname('test', 'NL'),
      hostname: `www-test-nl.eversports.dev`,
      localeSettings: localeNl,
    }),
    new CountryConfig({
      countryCode: 'FR',
      internalHostname: getInternalHostname('test', 'FR'),
      hostname: `www-test-fr.eversports.dev`,
      localeSettings: localeFr,
    }),
    new CountryConfig({
      countryCode: 'ES',
      internalHostname: getInternalHostname('test', 'ES'),
      hostname: `www-test-es.eversports.dev`,
      localeSettings: localeEs,
    }),
    new CountryConfig({
      countryCode: 'IT',
      internalHostname: getInternalHostname('test', 'IT'),
      hostname: `www-test-it.eversports.dev`,
      localeSettings: localeIt,
    }),
    new CountryConfig({
      countryCode: 'BE',
      internalHostname: getInternalHostname('test', 'BE'),
      hostname: `www-test-be.eversports.dev`,
      localeSettings: localeBe,
    }),
  ],
  production: [
    new CountryConfig({
      countryCode: 'AT',
      internalHostname: getInternalHostname('production', 'AT'),
      hostname: 'www.eversports.at',
      localeSettings: localeAt,
    }),
    new CountryConfig({
      countryCode: 'DE',
      internalHostname: getInternalHostname('production', 'DE'),
      hostname: 'www.eversports.de',
      localeSettings: localeDe,
    }),
    new CountryConfig({
      countryCode: 'CH',
      internalHostname: getInternalHostname('production', 'CH'),
      hostname: 'www.eversports.ch',
      localeSettings: localeCh,
    }),
    new CountryConfig({
      countryCode: 'NL',
      internalHostname: getInternalHostname('production', 'NL'),
      hostname: 'www.eversports.nl',
      localeSettings: localeNl,
    }),
    new CountryConfig({
      countryCode: 'FR',
      internalHostname: getInternalHostname('production', 'FR'),
      hostname: 'www.eversports.fr',
      localeSettings: localeFr,
    }),
    new CountryConfig({
      countryCode: 'ES',
      internalHostname: getInternalHostname('production', 'ES'),
      hostname: 'www.eversports.es',
      localeSettings: localeEs,
    }),
    new CountryConfig({
      countryCode: 'BE',
      internalHostname: getInternalHostname('production', 'BE'),
      hostname: 'www.eversports.be',
      localeSettings: localeBe,
    }),
    new CountryConfig({
      countryCode: 'IT',
      internalHostname: getInternalHostname('production', 'IT'),
      hostname: 'www.eversports.it',
      localeSettings: localeIt,
    }),
  ],
}

class CountryConfigs extends ImmutableMap<string, CountryConfig> {
  public get(key: string) {
    // TODO: look at second argument, should not be hard coded to be default AT
    const countryConfig = super.get((key || '').toUpperCase()) || super.get('AT')
    if (!countryConfig) {
      throw new Error('country config not found')
    }

    return countryConfig
  }
}

const countryConfigs = new CountryConfigs()
export const supportedCountryCodes: Array<string> = []
countryConfigMap[deployEnvironment].forEach((c) => {
  countryConfigs.set(c.countryCode, c)
  supportedCountryCodes.push(c.countryCode)
})
countryConfigs.freeze()

export default countryConfigs

export function getCountryConfigHostnamesForStage(stage: 'dev' | 'test' | 'production'): Array<string> {
  return countryConfigMap[stage].map((countryConfig) => countryConfig.hostname)
}

export function getCountryConfigForLanguage(language: string): CountryConfig {
  for (const config of countryConfigs.values()) {
    if (config.localeSettings.language === language) {
      return config
    }
  }

  return countryConfigs.get('AT')
}
